import React from 'react';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';

import { getDecorativeImage, getBucketClasses } from './utils';
import { BrandBucket } from '../../components/BrandBucket';
import { BrowseDataContext } from '../../context';
import { BrowseBucketContainer } from '../BrowseBucket';
import {
    DECORATIVE_IMAGE_PLACEMENT_MAP,
    NON_EVENT_BUCKET_SPACING_CLASSES,
    ORGANIZER_FOLLOW_TEMPLATE,
} from '../../../../constants';

import './mainContent.scss';

const SMALL_CITY_KEY = 'small_city_fallback';

const _getBuckets = (buckets, decorativeImages, fetchNextPage, hasNextPage) => {
    /* Creates an array to keep the used decorativeImages and prevent duplicates. */
    const usedDecorativeImages = [];

    return buckets.map((bucket = {}, index) => {
        const { template = '', key = '', name = '' } = bucket;

        /* DECORITIVE IMAGE LOGIC */
        const { decorativeImage, decorationPlacement } = getDecorativeImage(
            decorativeImages,
            key,
            usedDecorativeImages,
        );
        /* END DECORATIVE IMAGE LOGIC */

        /* CONTAINER CLASS LOGIC */
        const { bucketWrapperClasses, containerClasses } = getBucketClasses(
            key,
            decorativeImage,
            template,
        );
        /* END CONTAINER CLASS LOGIC */

        const shouldFetchMoreBuckets =
            hasNextPage && index === buckets.length - 1;

        return (
            <div key={name} className={`${key}--bucket-wrapper`}>
                <BrowseBucketContainer
                    containerClasses={containerClasses}
                    bucketWrapperClasses={bucketWrapperClasses}
                    decorationPlacement={decorationPlacement}
                    decorativeImage={decorativeImage}
                    bucketIndex={index}
                    onEnter={shouldFetchMoreBuckets ? fetchNextPage : undefined}
                    {...bucket}
                />
            </div>
        );
    });
};

const insertBrandBucket = (bucketSections, middleImage, middleSmallImage) => {
    if (!middleImage) {
        return bucketSections;
    }

    //We want to insert the large image midway down the page. But it shouldn't
    //be immediately next to one of our decorative images
    const midIndex = Math.floor(bucketSections.length / 2);
    const insertIndex = includes(
        Object.keys(DECORATIVE_IMAGE_PLACEMENT_MAP),
        `${midIndex}`,
    )
        ? midIndex + 1
        : midIndex;

    const middleImageComponent = (
        <div key="brand-image">
            <BrandBucket
                middleImage={middleImage}
                middleSmallImage={middleSmallImage}
            />
        </div>
    );

    return [
        ...bucketSections.slice(0, insertIndex),
        middleImageComponent,
        ...bucketSections.slice(insertIndex),
    ];
};

const organizerFollowBucket = {
    key: 'follow_organizer',
    name: 'Featured Organizers',
    template: ORGANIZER_FOLLOW_TEMPLATE,
    type: 'follow_organizer',
};

const insertFollowOrganizerBucket = (buckets, isLargeCity) => {
    const { key, name } = organizerFollowBucket;
    const insertIndex = isLargeCity ? 2 : 1;
    const FollowOrganizer = (
        <div key={name} className={`${key}--bucket-wrapper`}>
            <BrowseBucketContainer
                containerClasses={NON_EVENT_BUCKET_SPACING_CLASSES}
                {...organizerFollowBucket}
            />
        </div>
    );

    return [
        ...buckets.slice(0, insertIndex),
        FollowOrganizer,
        ...buckets.slice(insertIndex),
    ];
};

export const MainContent = (props) => {
    const { buckets, fetchNextPage, hasNextPage } = React.useContext(
        BrowseDataContext,
    );

    const {
        middleImage,
        middleSmallImage,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
    } = props;

    const decorativeImages = {
        decorationTopLeftUrl,
        decorationTopRightUrl,
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
    };

    /* Insert Follow Organizer Bucket into the response. Once
     backend does this for us will no longer be necessary */
    const [firstBucket] = buckets;
    const isLargeCity = firstBucket && firstBucket.key !== SMALL_CITY_KEY;

    const bucketSections = _getBuckets(
        buckets,
        decorativeImages,
        fetchNextPage,
        hasNextPage,
    );

    const bucketSectionsWithOrganizer = insertFollowOrganizerBucket(
        bucketSections,
        isLargeCity,
    );

    const bucketSectionsWithBrandAndOrganizer = insertBrandBucket(
        bucketSectionsWithOrganizer,
        middleImage,
        middleSmallImage,
    );

    return (
        <div className="main-content">
            {bucketSectionsWithBrandAndOrganizer}
        </div>
    );
};

MainContent.propTypes = {
    /*
     * Url for middle brand image
     */
    middleImage: PropTypes.string,
    /*
     * Url for middle brand image on small viewports
     */
    middleSmallImage: PropTypes.string,
    /*
     * Url for artist supplied decorative image
     */
    decorationTopLeftUrl: PropTypes.string,
    /*
     * Url for artist supplied decorative image
     */
    decorationTopRightUrl: PropTypes.string,
    /*
     * Url for artist supplied decorative image
     */
    decorationBottomLeftUrl: PropTypes.string,
    /*
     * Url for artist supplied decorative image
     */
    decorationBottomRightUrl: PropTypes.string,
};
